import { useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { contentAPI, dynamicAPI } from '../services/api';
import { useSiteSettings } from '../hooks/useContent';
import { usePageSeo } from '../hooks/usePageSeo';
import { buildArticleSchema, buildBreadcrumbSchema } from '../utils/seo';
import Breadcrumbs from '../components/Breadcrumbs';
import RichContent from '../components/RichContent';
import LoadingIndicator from '../components/LoadingIndicator';
import {
  Calendar,
  Clock,
  Share2,
  Copy,
  Check,
  Compass,
  BookOpen,
  ArrowRight,
  ArrowLeft
} from 'lucide-react';
import './BlogDetail.css';

const PLACEHOLDER_IMG =
  'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1600&q=80';

function formatPostDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return '';
  }
}

const BlogDetail = () => {
  const { slug } = useParams();
  const { settings } = useSiteSettings();
  const [copied, setCopied] = useState(false);

  const { data: blog, isLoading, isError, error } = useQuery(
    ['blog', slug],
    async () => {
      const res = await contentAPI.getBlogBySlug(slug);
      return res.data?.data;
    },
    {
      enabled: Boolean(slug),
      retry: false,
    }
  );

  // Fetch related/recent blogs for the sidebar & bottom section
  const { data: recentBlogs = [] } = useQuery(
    ['related-blogs', slug],
    async () => {
      try {
        const res = await contentAPI.getBlogs({ limit: 6 });
        const rows = res.data?.data || [];
        return rows.filter((b) => b.slug !== slug);
      } catch {
        return [];
      }
    },
    { staleTime: 5 * 60 * 1000 }
  );

  // Fetch upcoming treks for the sticky sidebar
  const { data: upcomingTreks = [] } = useQuery(
    ['sidebar-upcoming-treks'],
    async () => {
      try {
        const res = await dynamicAPI.getUpcomingTreks({ limit: 4 });
        return res.data?.data || [];
      } catch {
        return [];
      }
    },
    { staleTime: 5 * 60 * 1000 }
  );

  const publicBase =
    String(settings.site_public_url || '').replace(/\/$/, '') ||
    (typeof window !== 'undefined' ? window.location.origin : '');
  const blogSlug = blog?.slug || slug;
  const currentUrl =
    typeof window !== 'undefined'
      ? window.location.href
      : `${publicBase}/blogs/${blogSlug}`;

  const blogSchema = useMemo(() => {
    if (!blog) return null;
    const article = buildArticleSchema(blog, settings, publicBase);
    const breadcrumbs = buildBreadcrumbSchema([
      { name: 'Blogs & Articles', url: '/blogs' },
      { name: blog.title || 'Blog Post', url: `/blogs/${blogSlug}` }
    ], publicBase);
    return {
      '@context': 'https://schema.org',
      '@graph': [article, breadcrumbs].filter(Boolean)
    };
  }, [blog, settings, publicBase, blogSlug]);

  usePageSeo({
    enabled: Boolean(blog),
    type: 'article',
    title:
      (blog?.metaTitle && String(blog.metaTitle).trim()) ||
      (blog?.title ? `${blog.title} | Invincible NGO` : 'Blog'),
    description:
      (blog?.metaDescription && String(blog.metaDescription).trim()) ||
      (blog?.shortDescription && String(blog.shortDescription).trim()) ||
      settings.site_meta_description,
    canonical: blogSlug && publicBase ? `${publicBase}/blogs/${blogSlug}` : undefined,
    image: blog?.coverImageUrl || blog?.imageUrl || undefined,
    schema: blogSchema
  });

  // Calculate estimated reading time
  const readingTime = useMemo(() => {
    if (!blog?.content) return 3;
    const cleanText = String(blog.content).replace(/<[^>]*>/g, ' ');
    const words = cleanText.trim().split(/\s+/).filter(Boolean).length;
    return Math.max(1, Math.ceil(words / 200));
  }, [blog?.content]);

  const handleCopyLink = () => {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(currentUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    }
  };

  const shareTitle = blog?.title || 'Check out this story from Invincible NGO';
  const whatsappShareUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(shareTitle + ' - ' + currentUrl)}`;
  const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`;
  const twitterShareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTitle)}&url=${encodeURIComponent(currentUrl)}`;

  if (isLoading) {
    return (
      <div className="loading-state blog-post-loading">
        <LoadingIndicator label="Loading article…" />
      </div>
    );
  }

  if (isError || !blog) {
    const msg = error?.response?.status === 404 ? 'This post is unavailable or unpublished.' : null;
    return (
      <div className="empty-state blog-post-empty">
        <h2>Blog not found</h2>
        <p>{msg || 'The article you are looking for does not exist.'}</p>
        <Link to="/blogs" className="btn-primary">
          Browse all blogs
        </Link>
      </div>
    );
  }

  const heroImage = (blog.imageUrl && String(blog.imageUrl).trim()) || PLACEHOLDER_IMG;
  const categoryLabel = (blog.category && String(blog.category).trim()) || 'Heritage & Treks';
  const published = blog.publishedAt || blog.createdAt;
  const dateStr = formatPostDate(published);
  const authorName = (blog.author && String(blog.author).trim()) || 'Invincible Team';
  const authorInitial = authorName.charAt(0).toUpperCase();

  const sidebarRecent = recentBlogs.slice(0, 3);
  const bottomRecent = recentBlogs.slice(0, 3);

  return (
    <article className="blog-post-page">
      {/* Top Breadcrumbs Bar */}
      <div className="blog-top-breadcrumbs-bar">
        <div className="blog-container">
          <Breadcrumbs
            items={[
              { label: 'Blogs', path: '/blogs' },
              { label: blog.title || 'Blog Post' }
            ]}
            className="blog-breadcrumbs-nav"
          />
        </div>
      </div>

      {/* Editorial Article Hero Header (Full Width Cover Photo with Gradient & Title at bottom left) */}
      <header className="blog-hero-cover-header">
        <div className="blog-hero-cover-media">
          <img
            src={heroImage}
            alt={blog.title || 'Blog Cover'}
            className="blog-hero-cover-image"
            loading="eager"
          />
          <div className="blog-hero-cover-gradient" />
        </div>

        <div className="blog-container blog-hero-cover-content">
          <div className="blog-header-badge-row">
            <span className="blog-category-pill">{categoryLabel}</span>
            <span className="blog-reading-time">
              <Clock size={14} className="blog-icon-inline" />
              {readingTime} min read
            </span>
          </div>

          <h1 className="blog-article-title">{blog.title}</h1>

          {/* Author & Share Bar */}
          <div className="blog-author-share-bar">
            <div className="blog-author-meta">
              <div className="blog-author-avatar" aria-hidden="true">
                {authorInitial}
              </div>
              <div className="blog-author-details">
                <span className="blog-author-name">{authorName}</span>
                <span className="blog-post-date">
                  <Calendar size={13} className="blog-icon-inline" />
                  {dateStr || 'Recently published'}
                </span>
              </div>
            </div>

            {/* Social Share Controls */}
            <div className="blog-share-actions" aria-label="Share this story">
              <span className="blog-share-label">
                <Share2 size={14} />
                Share
              </span>
              <a
                href={whatsappShareUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="blog-share-btn blog-share-whatsapp"
                title="Share on WhatsApp"
                aria-label="Share on WhatsApp"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12.031 6.172c-3.181 0-5.767 2.586-5.768 5.766-.001 1.298.38 2.27 1.019 3.287l-.582 2.128 2.182-.573c.978.58 1.911.928 3.145.929 3.178 0 5.767-2.587 5.768-5.766.001-3.187-2.575-5.771-5.764-5.771zm3.392 8.244c-.144.405-.837.774-1.17.824-.299.045-.677.063-1.092-.069-.252-.08-.575-.187-.988-.365-1.739-.751-2.874-2.502-2.961-2.617-.087-.116-.708-.94-.708-1.793s.448-1.273.607-1.446c.159-.173.346-.217.462-.217l.332.007c.101.005.232-.039.362.275.144.346.491 1.199.534 1.286.043.087.072.188.014.304-.058.116-.087.188-.173.289l-.26.304c-.087.086-.177.18-.076.354.101.174.449.741.964 1.201.662.591 1.221.774 1.394.86s.275.072.376-.044c.101-.116.433-.506.549-.68.116-.173.231-.144.39-.086s1.011.477 1.184.564.289.13.332.202c.043.073.043.42-.101.825z" />
                </svg>
              </a>
              <a
                href={facebookShareUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="blog-share-btn blog-share-facebook"
                title="Share on Facebook"
                aria-label="Share on Facebook"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
                </svg>
              </a>
              <a
                href={twitterShareUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="blog-share-btn blog-share-twitter"
                title="Share on X"
                aria-label="Share on X"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </a>
              <button
                type="button"
                className={`blog-share-btn blog-share-copy ${copied ? 'copied' : ''}`}
                onClick={handleCopyLink}
                title={copied ? 'Link copied!' : 'Copy article link'}
                aria-label="Copy article link"
              >
                {copied ? <Check size={16} color="#16a34a" /> : <Copy size={16} />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main 2-Column Content + Sticky Sidebar */}
      <div className="blog-content-section">
        <div className="blog-container blog-editorial-grid">
          {/* Left Column: Article Body */}
          <div className="blog-main-content">
            {blog.shortDescription ? (
              <div className="blog-post-lead-card">
                <p className="blog-post-lead">{String(blog.shortDescription).trim()}</p>
              </div>
            ) : null}

            {blog.content ? (
              <RichContent content={blog.content} className="blog-post-prose" />
            ) : (
              <p className="blog-post-fallback">Full article text is coming soon.</p>
            )}

            {/* Back to all blogs navigation */}
            <div className="blog-nav-back-row">
              <Link to="/blogs" className="blog-back-btn">
                <ArrowLeft size={16} />
                Back to all stories & blogs
              </Link>
            </div>
          </div>

          {/* Right Column: Sticky Sidebar */}
          <aside className="blog-sticky-sidebar">
            {/* Widget 1: Upcoming Treks / Camps */}
            {upcomingTreks.length > 0 ? (
              <div className="blog-sidebar-card blog-sidebar-treks">
                <div className="blog-sidebar-card-header">
                  <div className="blog-sidebar-card-icon">
                    <Compass size={18} />
                  </div>
                  <div>
                    <h3 className="blog-sidebar-title">Upcoming Treks</h3>
                    <p className="blog-sidebar-subtitle">Next batches open for registration</p>
                  </div>
                </div>

                <div className="blog-sidebar-trek-list">
                  {upcomingTreks.map((card) => {
                    const trek = card.trek;
                    const batch = card.nextBatch;
                    const trekSlug = trek?.slug || card.slug || card.id;
                    const trekTitle = trek?.name || card.title || 'Expedition';
                    const trekImg =
                      trek?.media?.[0]?.url ||
                      card.imageUrl ||
                      PLACEHOLDER_IMG;
                    const price = batch?.price ? `₹${Number(batch.price).toLocaleString('en-IN')}` : null;

                    return (
                      <Link
                        key={trek?.id || card.id || trekSlug}
                        to={`/events/${trekSlug}`}
                        className="blog-sidebar-trek-item"
                      >
                        <img
                          src={trekImg}
                          alt={trekTitle}
                          className="blog-sidebar-trek-thumb"
                          loading="lazy"
                        />
                        <div className="blog-sidebar-trek-info">
                          <h4 className="blog-sidebar-trek-name">{trekTitle}</h4>
                          {price ? (
                            <span className="blog-sidebar-trek-price">Starting from {price}</span>
                          ) : (
                            <span className="blog-sidebar-trek-price">View Details</span>
                          )}
                        </div>
                      </Link>
                    );
                  })}
                </div>

                <Link to="/events" className="blog-sidebar-action-link">
                  View All Adventures
                  <ArrowRight size={15} />
                </Link>
              </div>
            ) : null}

            {/* Widget 2: Recent / Related Stories */}
            {sidebarRecent.length > 0 ? (
              <div className="blog-sidebar-card blog-sidebar-stories">
                <div className="blog-sidebar-card-header">
                  <div className="blog-sidebar-card-icon">
                    <BookOpen size={18} />
                  </div>
                  <div>
                    <h3 className="blog-sidebar-title">More Stories</h3>
                    <p className="blog-sidebar-subtitle">Related articles & experiences</p>
                  </div>
                </div>

                <div className="blog-sidebar-story-list">
                  {sidebarRecent.map((item) => (
                    <Link
                      key={item.id || item.slug}
                      to={`/blogs/${item.slug}`}
                      className="blog-sidebar-story-item"
                    >
                      <img
                        src={item.imageUrl || PLACEHOLDER_IMG}
                        alt={item.title}
                        className="blog-sidebar-story-thumb"
                        loading="lazy"
                      />
                      <div className="blog-sidebar-story-info">
                        <span className="blog-sidebar-story-cat">
                          {item.category || 'Article'}
                        </span>
                        <h4 className="blog-sidebar-story-name">{item.title}</h4>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ) : null}

          </aside>
        </div>
      </div>

      {/* Bottom Section: Related Articles Grid */}
      {bottomRecent.length > 0 ? (
        <section className="blog-bottom-related-section">
          <div className="blog-container">
            <div className="blog-bottom-related-header">
              <div>
                <span className="blog-bottom-pretitle">Explore More</span>
                <h2 className="blog-bottom-title">Related Stories & Guides</h2>
              </div>
              <Link to="/blogs" className="blog-bottom-view-all">
                Browse All Blogs
                <ArrowRight size={16} />
              </Link>
            </div>

            <div className="blog-bottom-related-grid">
              {bottomRecent.map((item) => (
                <Link
                  key={item.id || item.slug}
                  to={`/blogs/${item.slug}`}
                  className="blog-related-card"
                >
                  <div className="blog-related-card-img-wrap">
                    <img
                      src={item.imageUrl || PLACEHOLDER_IMG}
                      alt={item.title}
                      className="blog-related-card-img"
                      loading="lazy"
                    />
                    {item.category ? (
                      <span className="blog-related-card-tag">{item.category}</span>
                    ) : null}
                  </div>
                  <div className="blog-related-card-body">
                    <span className="blog-related-card-date">
                      {formatPostDate(item.publishedAt || item.createdAt)}
                    </span>
                    <h3 className="blog-related-card-title">{item.title}</h3>
                    {item.shortDescription ? (
                      <p className="blog-related-card-excerpt">
                        {String(item.shortDescription).slice(0, 110)}…
                      </p>
                    ) : null}
                    <span className="blog-related-card-link">
                      Read article <ArrowRight size={14} />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      ) : null}
    </article>
  );
};

export default BlogDetail;
